import {useCallback, useMemo} from 'react';
import {useLatest} from 'ahooks';
import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {
  awaitPendingUploadsForPromptBox,
  getInFlightPendingAttachments,
  getPendingImageAttachments,
} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';

export type PendingUploadAttachment = UserImageAttachment & {localId: string};

export interface UseAiPromptBoxPendingUploads {
  pendingAttachments: PendingUploadAttachment[];
  inFlightAttachments: PendingUploadAttachment[];
  hasPendingUploads: boolean;
  hasInFlightUploads: boolean;
  awaitAll: () => Promise<void>;
  getCurrentPendingSnapshot: () => PendingUploadAttachment[];
}

export const useAiPromptBoxPendingUploads = (id: string): UseAiPromptBoxPendingUploads => {
  const {state} = useAiPromptBox(id);
  const items = state.attachments?.items;
  const itemsRef = useLatest(items);

  const pendingAttachments = useMemo(() => getPendingImageAttachments(items), [items]);
  const inFlightAttachments = useMemo(() => getInFlightPendingAttachments(items), [items]);

  const awaitAll = useCallback((): Promise<void> => {
    return awaitPendingUploadsForPromptBox(id);
  }, [id]);

  const getCurrentPendingSnapshot = useCallback((): PendingUploadAttachment[] => {
    return getPendingImageAttachments(itemsRef.current);
  }, [itemsRef]);

  return {
    pendingAttachments,
    inFlightAttachments,
    hasPendingUploads: pendingAttachments.length > 0,
    hasInFlightUploads: inFlightAttachments.length > 0,
    awaitAll,
    getCurrentPendingSnapshot,
  };
};
